import { useEffect, useRef, useState } from "react";
import { isNativeShell, postJourney, postWatch, receiveFromShell, shellFetch, shellVersion } from "./native";
import {
	activityTokenIsFor,
	isStaleShell,
	type NativeActionReport,
	type NativeActivityToken,
	type NativeFix,
	type NativeJourney,
	type NativePinRequest,
	type NativePushToken,
} from "../shared/native-bridge";
import { nativeJourneyFrom, type NativeJourneyInput } from "../core/native-journey";
import { nativeWatchFrom, type NativeWatchInput } from "../core/native-watch";
import { report } from "./report";
import { activityTelemetryMark, type ActivityTelemetryMark } from "../core/activity-telemetry";

export interface NativeShell {
	/** True only inside the iOS host. In a browser everything here is inert. */
	active: boolean;
	/** The installed app is older than the contract this build speaks, so it owes a rebuild. */
	stale: boolean;
	/** The last position the shell sent, or null when the browser owns location. */
	fix: NativeFix | null;
	/**
	 * The shell's APNs token, or null in a browser and before Apple has answered.
	 *
	 * This is what lets the app be alerted without the Home-Screen PWA: it goes to `/api/alerts` in
	 * place of a push subscription, which a `WKWebView` can never hold.
	 */
	push: NativePushToken | null;
	/**
	 * The push token of the Live Activity currently on the lock screen, or null when there is none.
	 *
	 * A different address from `push`, and the difference is the point: that one reaches the app,
	 * this one reaches a single card. It goes to `/api/pin`, which is what lets the server correct
	 * the card while this page is suspended and cannot.
	 */
	activity: NativeActivityToken | null;
}

/**
 * What the shell tells us: fixes, taps, and its own failures.
 *
 * Split from the journey it is told because of where each has to sit. Location is needed near the
 * top of `App.tsx`, before there is a plan to describe; the journey can only be built once there
 * is. Hooks run in order, so one call could not do both.
 *
 * Everything here is a no-op in a browser: the receiver is installed and never called.
 */
export function useNativeLocation(onAction: (report: NativeActionReport) => void, onPin?: (pin: NativePinRequest) => void): NativeShell {
	const [fix, setFix] = useState<NativeFix | null>(null);
	const [push, setPush] = useState<NativePushToken | null>(null);
	const [activity, setActivity] = useState<NativeActivityToken | null>(null);
	const actionRef = useRef(onAction);
	actionRef.current = onAction;
	const pinRef = useRef(onPin);
	pinRef.current = onPin;

	useEffect(() => {
		return receiveFromShell((message) => {
			if (message.type === "fix") {
				setFix(message.fix);
				return;
			}
			if (message.type === "action") {
				actionRef.current(message.report);
				return;
			}
			if (message.type === "pin") {
				pinRef.current?.(message.pin);
				return;
			}
			if (message.type === "push-token") {
				// Re-sent on every page load, so most of these are the token already held.
				setPush((held) => (held?.token === message.push.token && held.environment === message.push.environment ? held : message.push));
				return;
			}
			if (message.type === "activity-token") {
				// Reissued while the card is running, so the newest always wins. The previous one is
				// dropped rather than kept: a push to a superseded token fails, and trying both would
				// spend twice the update budget to no purpose.
				setActivity((held) => (held?.token === message.activity.token ? held : message.activity));
				return;
			}
			// The shell has no console to fail in, so its own faults only exist if they are recorded.
			report("native-fault", { reason: message.fault.reason, atMs: message.fault.atMs, shell: shellVersion() });
		});
	}, []);

	return { active: isNativeShell(), stale: isStaleShell(shellVersion()), fix, push, activity };
}

/** Hands the journey to the shell whenever it changes. Silent in a browser. */
export function useNativeJourney(input: NativeJourneyInput | null, onReopenSent?: () => void, planId: string | null = null): void {
	const journey = input ? nativeJourneyFrom(input) : null;
	// `atMs` moves on every tick, so comparing the whole payload would post once a second.
	const signature = journey ? JSON.stringify({ ...journey, atMs: 0 }) : null;
	const journeyRef = useRef(journey);
	journeyRef.current = journey;
	const planIdRef = useRef(planId);
	planIdRef.current = planId;
	const telemetryRef = useRef<ActivityTelemetryMark | null>(null);
	const lastRef = useRef<string | null>(null);

	useEffect(() => {
		const current = journeyRef.current;
		if (!current || signature === lastRef.current) {
			return;
		}
		lastRef.current = signature;
		const submitted = postJourney(current);
		const mark = activityTelemetryMark(telemetryRef.current, current, planIdRef.current, Date.now());
		if (isNativeShell() && (mark || !submitted)) {
			if (mark) telemetryRef.current = mark;
			report("live-activity-bridge", { reason: "changed", submitted, planId: planIdRef.current, visibility: document.visibilityState, journey: current });
		}
		if (current.reopenActivity) {
			onReopenSent?.();
		}
	}, [signature, onReopenSent]);

	/*
	 * Say it again on the way back in.
	 *
	 * The activity may have been swiped away while this page was asleep, and a journey that has not
	 * changed since would never be sent again — so the lock screen would stay empty until the plan
	 * happened to move. Coming back to the app is the one moment worth repeating oneself.
	 */
	useEffect(() => {
		const resend = () => {
			const current = journeyRef.current;
			if (!current) {
				return;
			}
			if (document.visibilityState !== "visible") {
				if (isNativeShell()) report("live-activity-background", { planId: planIdRef.current, pending: lastRef.current !== JSON.stringify({ ...current, atMs: 0 }), journey: current });
				return;
			}
			lastRef.current = JSON.stringify({ ...current, atMs: 0 });
			const submitted = postJourney(current);
			if (isNativeShell()) report("live-activity-bridge", { reason: "visible", submitted, planId: planIdRef.current, visibility: document.visibilityState, journey: current });
		};
		document.addEventListener("visibilitychange", resend);
		return () => document.removeEventListener("visibilitychange", resend);
	}, []);
}

/**
 * Hands the wrist its pages whenever they change. Silent in a browser and on an unpaired phone.
 *
 * Separate from `useNativeJourney` rather than folded into it, because the two have different
 * reasons to be sent. The journey is a lock screen the shell may be holding open for hours; this is
 * a screen nobody is looking at until a wrist comes up, and it carries a deck that changes every
 * time a departure prediction moves. Sending them together would spend the Live Activity's update
 * budget on cards that are not on the Live Activity.
 */
export function useNativeWatch(input: NativeWatchInput | null): void {
	const watch = input ? nativeWatchFrom(input) : null;
	// `atMs` moves on every tick, so comparing the whole payload would post once a second.
	const signature = watch ? JSON.stringify({ ...watch, atMs: 0 }) : null;
	const watchRef = useRef(watch);
	watchRef.current = watch;
	const lastRef = useRef<string | null>(null);

	useEffect(() => {
		const current = watchRef.current;
		if (!current || signature === lastRef.current) {
			return;
		}
		lastRef.current = signature;
		postWatch(current);
	}, [signature]);

	/*
	 * Say it again on the way back in.
	 *
	 * The same reason the journey repeats itself: the phone may have been asleep while the watch was
	 * being read, and a payload that has not changed since would never be sent again.
	 */
	useEffect(() => {
		const resend = () => {
			const current = watchRef.current;
			if (!current || document.visibilityState !== "visible") {
				return;
			}
			lastRef.current = JSON.stringify({ ...current, atMs: 0 });
			postWatch(current);
		};
		document.addEventListener("visibilitychange", resend);
		return () => document.removeEventListener("visibilitychange", resend);
	}, []);
}

/**
 * How often the pin is renewed even when nothing about the journey has changed.
 *
 * Not a heartbeat for its own sake. The server ends a pin the device has stopped talking to, and
 * while the page is awake this is what proves it is still here. Five minutes is well inside that
 * deadline and costs one small request.
 */
const PIN_RENEW_MS = 5 * 60_000;

export interface JourneyPinInput {
	deviceId: string;
	/** What the card should be showing. Null outside a journey, which posts nothing. */
	journey: NativeJourney | null;
	profileId: string | null;
	targetMinutes: number | null;
	prepSeconds: number | null;
	stationBufferSeconds: number | null;
	destinationIndex: number;
	/** The train he has settled on, so the server re-plans against the same one. */
	ridePlanId: string | null;
	position: { lat: number; lon: number; accuracyMeters: number; speedMetersPerSecond: number | null } | null;
	activity: NativeActivityToken | null;
}

/**
 * Tell the server what this phone is in the middle of.
 *
 * The page stops running when the app goes into the background, which is the whole problem: the
 * Live Activity is only ever updated from here, so the card freezes at whatever was true when the
 * phone went in the pocket. Posting the pin hands the server everything it needs to carry on —
 * the words, the plan behind them, and the card's own push token.
 *
 * `planning` is excluded for the same reason the shell excludes it: the night before is not a
 * journey, and a pin for one would hold a deadline open until the sweep's expiry found it.
 * `arrived` is excluded because the journey is over — the renewal below would otherwise reopen a pin
 * the sweep had just closed, every five minutes, each time taking the card down again.
 */
export function useJourneyPin(input: JourneyPinInput | null): void {
	const active =
		input && input.journey && input.journey.phase !== "planning" && input.journey.phase !== "arrived"
			? (input as JourneyPinInput & { journey: NativeJourney })
			: null;
	/*
	 * The token has to name the card it belongs to, or it is not sent.
	 *
	 * `journeyKey` was carried the whole way down the bridge for this and nothing was checking it.
	 * The rule itself is in `activityTokenIsFor`, next to the contract it belongs to.
	 */
	const activity = active && activityTokenIsFor(active.activity, active.journey) ? active.activity : null;
	// `atMs` moves on every tick and the position moves constantly; neither is a reason to post.
	// What matters is the card's words, the plan behind them, and the address to correct it at.
	const signature = active
		? JSON.stringify({
				...active.journey,
				atMs: 0,
				profileId: active.profileId,
				targetMinutes: active.targetMinutes,
				prepSeconds: active.prepSeconds,
				stationBufferSeconds: active.stationBufferSeconds,
				destinationIndex: active.destinationIndex,
				ridePlanId: active.ridePlanId,
				activity: activity?.token ?? null,
			})
		: null;
	const inputRef = useRef<(JourneyPinInput & { journey: NativeJourney }) | null>(active);
	inputRef.current = active ? { ...active, activity } : null;
	const lastRef = useRef<{ signature: string; atMs: number } | null>(null);
	const telemetryRef = useRef<ActivityTelemetryMark | null>(null);

	useEffect(() => {
		const post = () => {
			const current = inputRef.current;
			if (!current || !signature) {
				return;
			}
			const now = Date.now();
			if (lastRef.current?.signature === signature && now - lastRef.current.atMs < PIN_RENEW_MS) {
				return;
			}
			lastRef.current = { signature, atMs: now };
			const mark = activityTelemetryMark(telemetryRef.current, current.journey, current.ridePlanId, now);
			const trace = isNativeShell() && mark != null;
			if (trace) telemetryRef.current = mark;
			const requestId = trace ? crypto.randomUUID() : null;
			const diagnostic = { requestId, planId: current.ridePlanId, journeyAtMs: current.journey.atMs, visibility: document.visibilityState };
			if (trace) report("pin-request", diagnostic);
			void shellFetch("/api/pin", {
				method: "PUT",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					requestId,
					deviceId: current.deviceId,
					journey: current.journey,
					plan: {
						profileId: current.profileId,
						targetMinutes: current.targetMinutes,
						prepSeconds: current.prepSeconds,
						stationBufferSeconds: current.stationBufferSeconds,
						destinationIndex: current.destinationIndex,
						ridePlanId: current.ridePlanId,
					},
					position: current.position,
					activity: current.activity,
				}),
				// The rider is not waiting on this and a failed pin is not worth an error path: the
				// next tick posts again, and the sweep's own diagnostics are where its absence shows.
			}).then((response) => {
				if (trace || (isNativeShell() && !response.ok)) report("pin-response", { ...diagnostic, status: response.status, elapsedMs: Date.now() - now });
			}).catch(() => {
				if (isNativeShell()) report("pin-response", { ...diagnostic, status: null, error: "network", elapsedMs: Date.now() - now });
			});
		};

		post();
		const timer = window.setInterval(post, 60_000);
		return () => window.clearInterval(timer);
	}, [signature]);
}
